import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { getRandomAiTypingDelay } from "../utils/aiTypingDelay";
import { useI18n } from "../i18n";
import ProfilePreview from "./ProfilePreview";
import OutOfTokensModal from "./OutOfTokensModal";
import BuyUnitsModal from "./BuyUnitsModal";
import "./Conversations.css";

const MESSAGE_COST = 20;
const CURRENT_USER_URL = "/api/users/me";
const NEAR_BOTTOM_THRESHOLD_PX = 120;
const SHOW_SCROLL_DOWN_THRESHOLD_PX = 120;

const TABS = [
  { key: "all", labelKey: "messages.all" },
  { key: "unread", labelKey: "messages.unread" },
  { key: "awaiting", labelKey: "messages.awaitingReply" }
];

function getInitial(username) {
  if (!username) {
    return "?";
  }

  return username.charAt(0).toUpperCase();
}

function getConversationPhoto(matchedUser) {
  if (!matchedUser) {
    return "";
  }

  return (
    matchedUser.mainPhotoUrl ||
    matchedUser.photoUrls?.[0] ||
    matchedUser.photoUrl ||
    ""
  );
}

function formatRelativeTime(dateValue) {
  if (!dateValue) {
    return "";
  }

  const then = new Date(dateValue).getTime();
  const now = Date.now();
  const diffMs = Math.max(now - then, 0);

  const minute = 60 * 1000;
  const hour = 60 * minute;
  const day = 24 * hour;

  if (diffMs < minute) {
    return "just now";
  }

  if (diffMs < hour) {
    const minutes = Math.floor(diffMs / minute);
    return `${minutes} min ago`;
  }

  if (diffMs < day) {
    const hours = Math.floor(diffMs / hour);
    return `${hours}h ago`;
  }

  const days = Math.floor(diffMs / day);

  if (days === 1) {
    return "1 day ago";
  }

  return `${days} days ago`;
}

export function ConversationListItem({ conversation, isActive, onOpen, onHide }) {
  const { t } = useI18n();
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const matchedUser = conversation.matchedUser || {};
  const photo = getConversationPhoto(matchedUser);
  const preview = conversation.lastMessageContent
    ? conversation.lastMessageContent
    : t("messages.startConversation");

  function handleAvatarProfileClick(event) {
    event.preventDefault();
    event.stopPropagation();
    setIsProfileOpen(true);
  }

  function handleAvatarProfileKeyDown(event) {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.stopPropagation();
      setIsProfileOpen(true);
    }
  }

  return (
    <div
      className={
        isActive
          ? "conversation-list-item conversation-list-item-active"
          : "conversation-list-item"
      }
      onClick={() => onOpen(conversation)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === "Enter" || event.key === " ") {
          onOpen(conversation);
        }
      }}
    >
      <button
        type="button"
        className="conversation-hide-button"
        aria-label={t("messages.hide")}
        title={t("messages.hide")}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          onHide(conversation);
        }}
      >
        ✕
      </button>

      <button
          type="button"
          className="conversation-avatar"
          onClick={handleAvatarProfileClick}
          onKeyDown={handleAvatarProfileKeyDown}
          aria-label={t("profilePreview.viewProfile")}
        >
          {photo ? (
            <img
              src={photo}
              alt={matchedUser.username || "User"}
              onError={(event) => {
                event.currentTarget.style.display = "none";
              }}
            />
          ) : (
            <span>{getInitial(matchedUser.username)}</span>
          )}

          <span
            className={
              matchedUser.online === true
                ? "conversation-online-dot"
                : "conversation-online-dot offline"
            }
          ></span>
      </button>

      <div className="conversation-details">
        <div className="conversation-name-row">
          <span className="conversation-name">
            <span
              className={
                matchedUser.online === true
                  ? "conversation-status-dot online"
                  : "conversation-status-dot"
              }
              title={matchedUser.online === true ? "Online" : "Offline"}
            ></span>
            <button
              type="button"
              className="conversation-name-profile-trigger profile-preview-trigger"
              onClick={handleAvatarProfileClick}
              onKeyDown={handleAvatarProfileKeyDown}
              aria-label={`${t("profilePreview.viewProfile")}: ${matchedUser.username || t("profilePreview.unknownUser")}`}
            >
              {matchedUser.username || "Unknown user"}
              {matchedUser.age ? `, ${matchedUser.age} years` : ""}
            </button>
          </span>

          <span className="conversation-time">
            {formatRelativeTime(conversation.lastMessageAt || conversation.createdAt)}
          </span>
        </div>

        <div className="conversation-preview-row">
          <span className="conversation-city">
            {matchedUser.city || "Location not provided"}
          </span>
        </div>

        <div className="conversation-preview-row">
          <span className="conversation-preview-text">{preview}</span>

          {conversation.unreadCount > 0 && (
            <span className="conversation-unread-badge">{conversation.unreadCount}</span>
          )}
        </div>
      </div>

      <ProfilePreview
        matchedUser={matchedUser}
        isOpen={isProfileOpen}
        onClose={() => setIsProfileOpen(false)}
        hideTrigger
      />
    </div>
  );
}

export function ConversationsList({
  conversations,
  loading,
  error,
  activeMatchId,
  onOpen,
  onHide
}) {
  const { t } = useI18n();
  const [activeTab, setActiveTab] = useState("all");

  const counts = useMemo(() => {
    return {
      all: conversations.length,
      unread: conversations.filter((c) => c.unreadCount > 0).length,
      awaiting: conversations.filter((c) => c.awaitingMyReply).length
    };
  }, [conversations]);

  const filteredConversations = useMemo(() => {
    if (activeTab === "unread") {
      return conversations.filter((c) => c.unreadCount > 0);
    }

    if (activeTab === "awaiting") {
      return conversations.filter((c) => c.awaitingMyReply);
    }

    return conversations;
  }, [conversations, activeTab]);

  return (
    <div className="conversations-sidebar">
      <div className="conversations-sidebar-header">
        <h1 className="conversations-title">
          <span className="conversations-title-icon" aria-hidden="true">✉️</span>
          {t("messages.title")}
        </h1>
      </div>

      <div className="conversations-tabs">
        {TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={
              activeTab === tab.key
                ? "conversations-tab conversations-tab-active"
                : "conversations-tab"
            }
            onClick={() => setActiveTab(tab.key)}
          >
            {t(tab.labelKey)}
            <span className="conversations-tab-count">{counts[tab.key]}</span>
          </button>
        ))}
      </div>

      <div className="conversations-list">
        {loading && (
          <div className="conversations-empty">{t("messages.loading")}</div>
        )}

        {!loading && error && (
          <div className="conversations-empty conversations-error">{error}</div>
        )}

        {!loading && !error && filteredConversations.length === 0 && (
          <div className="conversations-empty">
            <p>{t("messages.empty")}</p>
            <Link to="/matches" className="browse-link-button">
              {t("messages.viewMatches")}
            </Link>
          </div>
        )}

        {!loading &&
          !error &&
          filteredConversations.map((conversation) => (
            <ConversationListItem
              key={conversation.matchId}
              conversation={conversation}
              isActive={conversation.matchId === activeMatchId}
              onOpen={onOpen}
              onHide={onHide}
            />
          ))}
      </div>

      {!activeMatchId && (
        <div className="conversations-selection-placeholder">
          <p>{t("messages.chooseConversation")}</p>
        </div>
      )}
    </div>
  );
}

function ChatPanel({ matchId, pendingTyping, onPendingTypingChange }) {
  const { t } = useI18n();
  const { token, user, logout, updateUser } = useAuth();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [matchId]);

  const [messages, setMessages] = useState([]);
  const [matchedUser, setMatchedUser] = useState(null);
  const [content, setContent] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [waitingForAiReply, setWaitingForAiReply] = useState(false);
  const [newMessagesAvailable, setNewMessagesAvailable] = useState(false);
  const [tokenBalance, setTokenBalance] = useState(user?.tokenBalance ?? 0);

  const messagesPanelRef = useRef(null);
  const shouldAutoScrollRef = useRef(true);
  const hasScrolledOnLoadRef = useRef(false);
  const messageCountRef = useRef(0);
  const forceScrollToBottomRef = useRef(false);
  const programmaticScrollRef = useRef(false);
  const scrollFrameRef = useRef(null);
  const incomingMessageIdsBeforeWaitingRef = useRef(new Set());
  const typingDelayTimeoutRef = useRef(null);

  const hasEnoughTokens = tokenBalance >= MESSAGE_COST;
  const [tokensPopupOpen, setTokensPopupOpen] = useState(false);
  const [buyPackCode, setBuyPackCode] = useState(null);
  const tokensPopupDismissedForRef = useRef(null);

  useEffect(() => {
    if (!hasEnoughTokens && tokensPopupDismissedForRef.current !== matchId) {
      setTokensPopupOpen(true);
    }
  }, [hasEnoughTokens, matchId]);

  function closeTokensPopup() {
    tokensPopupDismissedForRef.current = matchId;
    setTokensPopupOpen(false);
  }

  const isMyMessage = useCallback(
    function isMyMessage(message) {
      return message.senderId === user?.id || message.senderId === user?._id;
    },
    [user?.id, user?._id]
  );

  const hasReplyForPendingMessage = useCallback(
    (messageList, pendingMessage) => {
      if (!pendingMessage?.messageId) {
        return false;
      }

      const outgoingIndex = messageList.findIndex(
        (message) =>
          message.id === pendingMessage.messageId &&
          (message.senderId === pendingMessage.senderId ||
            pendingMessage.senderId === undefined)
      );

      if (outgoingIndex < 0) {
        return false;
      }

      return messageList
        .slice(outgoingIndex + 1)
        .some((message) => !isMyMessage(message));
    },
    [isMyMessage]
  );

  const refreshMatchedUser = useCallback(
    async function refreshMatchedUser(headers) {
      if (!matchId || !headers) {
        return;
      }

      try {
        const matchResponse = await axios.get(`/api/matches/${matchId}`, {
          headers
        });

        const freshMatchedUser = matchResponse.data?.matchedUser;

        if (freshMatchedUser) {
          setMatchedUser(freshMatchedUser);
        }
      } catch (err) {
        console.error("Fresh matched user load error:", err);
      }
    },
    [matchId]
  );

  const fetchMessagesOnly = useCallback(
    async function fetchMessagesOnly(headers) {
      if (!matchId || !headers) {
        return [];
      }

      const response = await axios.get(`/api/messages/${matchId}`, {
        headers
      });

      const freshMessages = Array.isArray(response.data) ? response.data : [];

      setMessages(freshMessages);

      return freshMessages;
    },
    [matchId]
  );

  useEffect(() => {
    setTokenBalance(user?.tokenBalance ?? 0);
  }, [user?.tokenBalance]);

  useEffect(() => {
    let cancelled = false;

    hasScrolledOnLoadRef.current = false;
    messageCountRef.current = 0;
    setNewMessagesAvailable(false);
    clearPendingTypingDelay();
    setWaitingForAiReply(false);
    incomingMessageIdsBeforeWaitingRef.current = new Set();

    async function fetchChatData() {
      if (!token || !matchId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");
        setMatchedUser(null);

        const headers = {
          Authorization: `Bearer ${token}`
        };

        const messagesResponse = await axios.get(`/api/messages/${matchId}`, {
          headers
        });

        if (cancelled) {
          return;
        }

        const freshMessages = Array.isArray(messagesResponse.data)
          ? messagesResponse.data
          : [];

        setMessages(freshMessages);

        if (hasReplyForPendingMessage(freshMessages, pendingTyping)) {
          onPendingTypingChange(null);
        }

        await refreshMatchedUser(headers);

        try {
          const userResponse = await axios.get(CURRENT_USER_URL, { headers });

          if (cancelled) {
            return;
          }

          const freshBalance = userResponse.data?.tokenBalance ?? 0;
          setTokenBalance(freshBalance);
        } catch (balanceErr) {
          console.error("Balance load error:", balanceErr);

          if (!cancelled) {
            setTokenBalance(user?.tokenBalance ?? 0);
          }
        }
      } catch (err) {
        if (cancelled) {
          return;
        }

        console.error("Chat load error:", err);

        if (err.response && err.response.status === 401) {
          await logout();
          setError("Session expired. Please login again.");
        } else if (err.response && err.response.status === 403) {
          setError("You are not allowed to view this chat.");
        } else if (err.response && err.response.data) {
          setError(
            typeof err.response.data === "string"
              ? err.response.data
              : "Failed to load chat."
          );
        } else {
          setError("Failed to load chat.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    fetchChatData();

    return () => {
      cancelled = true;
    };
  }, [
    matchId,
    token,
    logout,
    refreshMatchedUser,
    hasReplyForPendingMessage,
    onPendingTypingChange
  ]);

  useEffect(() => {
    clearPendingTypingDelay();

    if (!pendingTyping) {
      setWaitingForAiReply(false);
      return undefined;
    }

    const remainingMs = Math.max(pendingTyping.dueAt - Date.now(), 0);

    if (remainingMs === 0) {
      setWaitingForAiReply(true);
      return undefined;
    }

    setWaitingForAiReply(false);
    typingDelayTimeoutRef.current = setTimeout(() => {
      typingDelayTimeoutRef.current = null;
      setWaitingForAiReply(true);
    }, remainingMs);

    return () => {
      clearPendingTypingDelay();
    };
  }, [pendingTyping, matchId]);

  useEffect(() => {
    if (!token || !matchId) {
      return;
    }

    const headers = {
      Authorization: `Bearer ${token}`
    };

    const intervalId = setInterval(async () => {
      try {
        const freshMessages = await fetchMessagesOnly(headers);

        const previousIncomingIds = incomingMessageIdsBeforeWaitingRef.current;

        const hasNewIncomingMessage = freshMessages.some((message) => {
          const mine = isMyMessage(message);

          if (mine || !message.id) {
            return false;
          }

          return !previousIncomingIds.has(message.id);
        });

        if (hasNewIncomingMessage) {
          clearPendingTypingDelay();
          setWaitingForAiReply(false);
          onPendingTypingChange(null);
          incomingMessageIdsBeforeWaitingRef.current = new Set();
        }
      } catch (err) {
        console.error("Polling messages failed:", err);
      }
    }, 5000);

    return () => {
      clearInterval(intervalId);
    };
  }, [
    token,
    matchId,
    waitingForAiReply,
    fetchMessagesOnly,
    isMyMessage,
    onPendingTypingChange
  ]);

  useEffect(() => {
    if (!token || !matchId) {
      return;
    }

    const headers = {
      Authorization: `Bearer ${token}`
    };

    const intervalId = setInterval(() => {
      refreshMatchedUser(headers);
    }, 30000);

    return () => {
      clearInterval(intervalId);
    };
  }, [token, matchId, refreshMatchedUser]);

  useEffect(() => {
    if (!waitingForAiReply) {
      return;
    }

    const timeoutId = setTimeout(() => {
      setWaitingForAiReply(false);
      incomingMessageIdsBeforeWaitingRef.current = new Set();
    }, 75000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [waitingForAiReply]);

  useEffect(() => {
    return () => {
      clearPendingTypingDelay();
      setWaitingForAiReply(false);
      incomingMessageIdsBeforeWaitingRef.current = new Set();
      if (scrollFrameRef.current !== null) {
        cancelAnimationFrame(scrollFrameRef.current);
      }
    };
  }, [matchId]);

  useEffect(() => {
    const hasNewMessages = messages.length > messageCountRef.current;
    messageCountRef.current = messages.length;
    const forceScroll = forceScrollToBottomRef.current;
    forceScrollToBottomRef.current = false;

    if (!forceScroll && !hasNewMessages && !waitingForAiReply) {
      return;
    }

    if (forceScroll || shouldAutoScrollRef.current || !hasScrolledOnLoadRef.current) {
      scrollToBottom(forceScroll);
    } else {
      setNewMessagesAvailable(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, waitingForAiReply]);

  function handleMessagesScroll() {
    const panel = messagesPanelRef.current;

    if (!panel || programmaticScrollRef.current) {
      return;
    }

    const distanceFromBottom =
      panel.scrollHeight - panel.scrollTop - panel.clientHeight;

    shouldAutoScrollRef.current = distanceFromBottom <= NEAR_BOTTOM_THRESHOLD_PX;
    setNewMessagesAvailable(
      distanceFromBottom > SHOW_SCROLL_DOWN_THRESHOLD_PX
    );
  }

  function scrollToBottom(force = false) {
    const panel = messagesPanelRef.current;

    if (!panel) {
      return;
    }

    if (scrollFrameRef.current !== null) {
      cancelAnimationFrame(scrollFrameRef.current);
    }

    programmaticScrollRef.current = true;
    let attempts = 0;
    const applyScroll = () => {
      attempts += 1;

      if (!force && !shouldAutoScrollRef.current) {
        scrollFrameRef.current = null;
        programmaticScrollRef.current = false;
        return;
      }

      panel.scrollTop = Math.max(0, panel.scrollHeight - panel.clientHeight);

      if (attempts < 4) {
        scrollFrameRef.current = requestAnimationFrame(applyScroll);
        return;
      }

      scrollFrameRef.current = null;
      hasScrolledOnLoadRef.current = true;
      setNewMessagesAvailable(false);

      requestAnimationFrame(() => {
        programmaticScrollRef.current = false;
      });
    };

    scrollFrameRef.current = requestAnimationFrame(() => {
      scrollFrameRef.current = requestAnimationFrame(() => {
        applyScroll();
      });
    });
  }

  function handleNewMessagesClick() {
    shouldAutoScrollRef.current = true;
    setNewMessagesAvailable(false);
    scrollToBottom(true);
  }

  function rememberCurrentIncomingMessageIds(messageList) {
    const existingIncomingMessageIds = new Set();

    messageList.forEach((message) => {
      const mine = isMyMessage(message);

      if (!mine && message.id) {
        existingIncomingMessageIds.add(message.id);
      }
    });

    incomingMessageIdsBeforeWaitingRef.current = existingIncomingMessageIds;
  }

  function clearPendingTypingDelay() {
    if (typingDelayTimeoutRef.current !== null) {
      clearTimeout(typingDelayTimeoutRef.current);
      typingDelayTimeoutRef.current = null;
    }
  }

  function getOnlineText() {
    if (waitingForAiReply) {
      return "Typing...";
    }

    return matchedUser?.online === true ? "Online" : "Offline";
  }

  function formatDate(dateValue) {
    if (!dateValue) {
      return "";
    }

    return new Date(dateValue).toLocaleString();
  }

  async function handleSubmit(event) {
    event.preventDefault();

    if (sending) {
      return;
    }

    const trimmedContent = content.trim();

    if (!trimmedContent) {
      return;
    }

    if (!hasEnoughTokens) {
      return;
    }

    try {
      setSending(true);
      shouldAutoScrollRef.current = true;
      forceScrollToBottomRef.current = true;
      setNewMessagesAvailable(false);
      setError("");

      const headers = {
        Authorization: `Bearer ${token}`
      };

      const response = await axios.post(
        `/api/messages/${matchId}`,
        { content: trimmedContent },
        { headers }
      );

      const sendResponse = response.data;

      const userMessage = sendResponse?.userMessage ?? sendResponse;
      const aiReplyPending = sendResponse?.aiReplyPending === true;

      const newBalance =
        sendResponse?.remainingTokenBalance ?? userMessage?.remainingTokenBalance;

      const chargedAmount =
        sendResponse?.messageCost ?? userMessage?.messageCost ?? MESSAGE_COST;

      if (userMessage && userMessage.id) {
        setMessages((previousMessages) => {
          rememberCurrentIncomingMessageIds(previousMessages);
          return [...previousMessages, userMessage];
        });
      } else {
        const refreshedMessagesResponse = await axios.get(
          `/api/messages/${matchId}`,
          { headers }
        );

        const refreshedMessages = Array.isArray(refreshedMessagesResponse.data)
          ? refreshedMessagesResponse.data
          : [];

        rememberCurrentIncomingMessageIds(refreshedMessages);
        setMessages(refreshedMessages);
      }

      if (aiReplyPending) {
        const delaySeconds =
          sendResponse?.aiReplyDelaySeconds ?? getRandomAiTypingDelay() / 1000;
        clearPendingTypingDelay();
        setWaitingForAiReply(false);
        onPendingTypingChange({
          messageId: userMessage?.id ?? null,
          senderId: userMessage?.senderId ?? user?.id ?? user?._id,
          dueAt: Date.now() + delaySeconds * 1000
        });
      } else {
        clearPendingTypingDelay();
        setWaitingForAiReply(false);
        onPendingTypingChange(null);
        incomingMessageIdsBeforeWaitingRef.current = new Set();
      }

      await refreshMatchedUser(headers);

      if (newBalance !== null && newBalance !== undefined) {
        setTokenBalance(newBalance);

        if (updateUser) {
          updateUser({ tokenBalance: newBalance });
        }
      } else {
        const fallbackBalance = Math.max(tokenBalance - chargedAmount, 0);

        setTokenBalance(fallbackBalance);

        if (updateUser) {
          updateUser({ tokenBalance: fallbackBalance });
        }
      }

      setContent("");
    } catch (err) {
      console.error("Send message error:", err);

      if (err.response && err.response.status === 401) {
        await logout();
        setError("Session expired. Please login again.");
      } else if (err.response && err.response.status === 402) {
        setTokenBalance(0);

        if (updateUser) {
          updateUser({ tokenBalance: 0 });
        }

        setError("");
      } else if (err.response && err.response.data) {
        setError(
          typeof err.response.data === "string"
            ? err.response.data
            : "Failed to send message."
        );
      } else {
        setError("Failed to send message.");
      }
    } finally {
      setSending(false);
    }
  }

  if (!matchId) {
    return (
      <div className="chat-panel chat-panel-placeholder">
        <p>{t("messages.chooseConversation")}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="chat-panel">
        <div className="chat-panel-loading">Loading conversation...</div>
      </div>
    );
  }

  return (
    <div className="chat-panel">
      <div className="chat-panel-header">
        <Link to="/messages" className="chat-back-link" aria-label="Back to messages list">
          ←
        </Link>

        <ProfilePreview matchedUser={matchedUser} className="chat-panel-user-info">
          <div className="chat-avatar-wrap">
            <div className="chat-avatar">
              {getConversationPhoto(matchedUser) ? (
                <img
                  src={getConversationPhoto(matchedUser)}
                  alt={matchedUser?.username}
                  className="chat-avatar-image"
                  onError={(event) => {
                    event.currentTarget.style.display = "none";
                  }}
                />
              ) : (
                <span>{getInitial(matchedUser?.username)}</span>
              )}
            </div>
            <span
              className={
                matchedUser?.online === true
                  ? "chat-status-dot online"
                  : "chat-status-dot offline"
              }
            ></span>
          </div>

          <div className="chat-title-wrap">
            <h2 className="chat-panel-title">
              {matchedUser?.username || "Chat"}
            </h2>

            <span
              className={
                waitingForAiReply
                  ? "chat-status-badge typing"
                  : matchedUser?.online === true
                    ? "chat-status-badge online"
                    : "chat-status-badge offline"
              }
            >
              <span className="chat-status-badge-dot"></span>
              <span>{getOnlineText()}</span>
            </span>
          </div>
        </ProfilePreview>
      </div>

      {!hasEnoughTokens && (
        <div className="token-card token-card-warning-only">
          <button type="button" className="token-warning" onClick={() => setTokensPopupOpen(true)}>
            {t("messages.outOfTokens")}
          </button>
        </div>
      )}

      <OutOfTokensModal
        isOpen={tokensPopupOpen && !hasEnoughTokens}
        onClose={closeTokensPopup}
        onBuy={(code) => {
          closeTokensPopup();
          setBuyPackCode(code);
        }}
      />
      <BuyUnitsModal isOpen={Boolean(buyPackCode)} initialPackCode={buyPackCode} onClose={() => setBuyPackCode(null)} />

      {error && <div className="chat-message chat-error">{error}</div>}

      <div className="messages-panel-wrap">
        <div
          className="messages-panel"
          ref={messagesPanelRef}
          onScroll={handleMessagesScroll}
        >

        {messages.length === 0 && (
          <div className="chat-empty">
            <h2>No messages yet</h2>
            <p>{t("messages.startConversation")}</p>
          </div>
        )}

        {messages.map((message) => {
          const mine = isMyMessage(message);

          return (
            <div
              key={message.id}
              className={mine ? "message-row mine" : "message-row theirs"}
            >
              <div className={mine ? "message-bubble mine" : "message-bubble theirs"}>
                <div className="message-sender">
                  {mine ? (
                    "You"
                  ) : (
                    <span className="message-sender-profile">
                      <span className="message-sender-avatar-wrap">
                        <span className="message-sender-avatar">
                          {getConversationPhoto(matchedUser) ? (
                            <img
                              src={getConversationPhoto(matchedUser)}
                              alt=""
                              className="message-sender-avatar-image"
                            />
                          ) : (
                            getInitial(message.senderUsername || matchedUser?.username)
                          )}
                        </span>
                        <span
                          className={
                            matchedUser?.online === true
                              ? "message-sender-status online"
                              : "message-sender-status"
                          }
                        ></span>
                      </span>
                      <span>{message.senderUsername || matchedUser?.username || "User"}</span>
                    </span>
                  )}
                  </div>

                <div className="message-content">{message.content}</div>

                <div className="message-time">{formatDate(message.createdAt)}</div>
              </div>
            </div>
          );
        })}

        {waitingForAiReply && (
          <div className="message-row theirs">
            <div className="message-bubble theirs typing-bubble">
              <div className="message-sender">
                <span className="message-sender-profile">
                  <span className="message-sender-avatar-wrap">
                    <span className="message-sender-avatar">
                      {getConversationPhoto(matchedUser) ? (
                        <img
                          src={getConversationPhoto(matchedUser)}
                          alt=""
                          className="message-sender-avatar-image"
                        />
                      ) : (
                        getInitial(matchedUser?.username)
                      )}
                    </span>
                    <span
                      className={
                        matchedUser?.online === true
                          ? "message-sender-status online"
                          : "message-sender-status"
                      }
                    ></span>
                  </span>
                  <span>{matchedUser?.username || "User"}</span>
                </span>
              </div>

              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
                <strong>Typing...</strong>
              </div>
            </div>
          </div>
        )}
        </div>
        {newMessagesAvailable && (
          <button
            type="button"
            className="new-messages-button"
            onClick={handleNewMessagesClick}
            aria-label="Go to latest messages"
            title="Go to latest messages"
          >
            ↓
          </button>
        )}
      </div>

      <form className="chat-form" onSubmit={handleSubmit}>
        <input
          type="text"
          className="chat-input"
          value={content}
          onChange={(event) => setContent(event.target.value)}
          placeholder={
            hasEnoughTokens
              ? "Type your message..."
              : "Top-up required to send messages"
          }
          disabled={!hasEnoughTokens || sending}
        />

        <button
          type="submit"
          className="chat-send-button"
          disabled={!hasEnoughTokens || sending || !content.trim()}
        >
          {sending ? t("messages.sending") : t("messages.send")}
        </button>
      </form>
    </div>
  );
}

function Conversations() {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const { token, logout } = useAuth();

  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [pendingTypingByMatchId, setPendingTypingByMatchId] = useState({});

  const loadConversations = useCallback(
    async function loadConversations() {
      if (!token) {
        setError("You must be logged in to view this page.");
        setLoading(false);
        return;
      }

      try {
        setError("");

        const response = await axios.get("/api/matches/conversations", {
          headers: { Authorization: `Bearer ${token}` }
        });

        setConversations(Array.isArray(response.data) ? response.data : []);
      } catch (err) {
        console.error("Conversations load error:", err);

        if (err.response && err.response.status === 401) {
          logout();
          setError("Session expired. Please login again.");
        } else {
          setError("Failed to load your conversations.");
        }
      } finally {
        setLoading(false);
      }
    },
    [token, logout]
  );

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Keep the conversation list (previews, unread counts) fresh while a chat
  // is open, so unread badges and last-message text stay in sync.
  useEffect(() => {
    if (!token) {
      return;
    }

    const intervalId = setInterval(() => {
      loadConversations();
    }, 15000);

    return () => {
      clearInterval(intervalId);
    };
  }, [token, loadConversations]);

  function handleOpen(conversation) {
    navigate(`/messages/${conversation.matchId}`, {
      state: { matchedUser: conversation.matchedUser }
    });

    setConversations((previous) =>
      previous.map((item) =>
        item.matchId === conversation.matchId
          ? { ...item, unreadCount: 0 }
          : item
      )
    );
  }

  const handlePendingTypingChange = useCallback(
    (nextPendingTyping) => {
      if (!matchId) {
        return;
      }

      setPendingTypingByMatchId((previous) => {
        if (!nextPendingTyping) {
          if (!previous[matchId]) {
            return previous;
          }

          const next = { ...previous };
          delete next[matchId];
          return next;
        }

        return {
          ...previous,
          [matchId]: nextPendingTyping
        };
      });
    },
    [matchId]
  );

  async function handleHide(conversation) {
    const confirmed = window.confirm(
      `Hide the conversation with ${conversation.matchedUser?.username || "this user"}? You can find them again from Matches.`
    );

    if (!confirmed) {
      return;
    }

    try {
      await axios.delete(`/api/matches/${conversation.matchId}/hide`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setConversations((previous) =>
        previous.filter((item) => item.matchId !== conversation.matchId)
      );

      if (matchId === conversation.matchId) {
        navigate("/messages");
      }
    } catch (err) {
      console.error("Hide conversation error:", err);
      window.alert("Failed to hide this conversation. Please try again.");
    }
  }

  return (
    <div className={`conversations-page ${matchId ? "conversations-page-chat-open" : ""}`}>
      <ConversationsList
        conversations={conversations}
        loading={loading}
        error={error}
        activeMatchId={matchId}
        onOpen={handleOpen}
        onHide={handleHide}
      />

      <div className="conversations-main">
        {matchId ? (
          <ChatPanel
            matchId={matchId}
            pendingTyping={pendingTypingByMatchId[matchId]}
            onPendingTypingChange={handlePendingTypingChange}
          />
        ) : null}
      </div>
    </div>
  );
}

export default Conversations;
